/**
 * Paste a hash, an address or an account and land on its page
 * (blackbeard/observer#49).
 *
 * The site's pages were reachable only by following links. This is the front
 * door for someone who arrives holding a transaction hash from a wallet or an
 * address from a chat message.
 *
 * It searches on Enter, never while typing: every search costs lookups, one of
 * them on the node, and half-typed hashes resolve to nothing. The server
 * confirms each candidate against data rather than guessing from shape — one
 * string can honestly be a block hash and a miner's preimage both — so:
 *
 * - **one hit** goes straight there;
 * - **several** are listed for the reader to choose, never chosen for them;
 * - **none** says what the string was checked as, so a miss reads as an answer
 *   and not as the site being broken.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import type { SearchHit } from '../api/generated/SearchHit'
import type { SearchResults } from '../api/generated/SearchResults'
import { RequestFailed, fetchSearch } from '../api/rest'
import { href } from '../lib/routes'

/** Where a hit's page is. */
function hitHref(chain: string, h: SearchHit): string {
  switch (h.kind) {
    case 'block':
      return href({ chain, block: h.value })
    case 'tx':
      return href({ chain, tx: h.value })
    case 'account':
      return href({ chain, account: h.value })
    case 'miner':
      return href({ chain, miner: h.value })
    case 'node':
      return href({ chain, node: h.value })
    case 'runtime':
      return href({ chain, runtime: Number(h.value) })
    case 'near_tx':
      return href({ chain, nearTx: h.value })
    case 'intents_account':
      return href({ chain, intentsAccount: h.value })
    default:
      return href({ chain })
  }
}

export function SearchBox({ chain }: { chain: string }) {
  const navigate = useNavigate()
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [results, setResults] = useState<SearchResults | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [active, setActive] = useState(0)
  const box = useRef<HTMLDivElement>(null)
  const pending = useRef<AbortController | null>(null)

  const close = () => {
    setResults(null)
    setError(null)
  }

  // A click anywhere else closes the list, as a menu would.
  useEffect(() => {
    if (!results && !error) return
    const outside = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [results, error])

  const go = (h: SearchHit) => {
    navigate(hitHref(chain, h))
    setText('')
    close()
  }

  const search = () => {
    const q = text.trim()
    if (!q) return
    pending.current?.abort()
    const controller = new AbortController()
    pending.current = controller
    setBusy(true)
    setError(null)
    fetchSearch(chain, q, controller.signal)
      .then((r) => {
        if (r.hits.length === 1 && r.hits[0]) {
          go(r.hits[0])
          return
        }
        setResults(r)
        setActive(0)
      })
      .catch((e: unknown) => {
        if (controller.signal.aborted) return
        setResults(null)
        setError(e instanceof RequestFailed ? e.message : 'Could not reach the observer.')
      })
      .finally(() => setBusy(false))
  }

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const hits = results?.hits ?? []
    if (e.key === 'Escape') {
      if (results || error) close()
      else setText('')
      return
    }
    if (e.key === 'ArrowDown' && hits.length > 0) {
      e.preventDefault()
      setActive((a) => (a + 1) % hits.length)
      return
    }
    if (e.key === 'ArrowUp' && hits.length > 0) {
      e.preventDefault()
      setActive((a) => (a - 1 + hits.length) % hits.length)
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      const chosen = hits[active]
      // Enter on an open list picks the highlighted hit; otherwise it searches.
      if (results && hits.length > 1 && chosen) go(chosen)
      else search()
    }
  }

  const listId = 'search-results'
  const open = results !== null || error !== null

  return (
    <div className="search" ref={box}>
      <input
        type="search"
        className="search-input"
        placeholder="Block, transaction, address or account"
        aria-label="Search this chain: paste a block, transaction, address or account"
        value={text}
        onChange={(e) => {
          setText(e.target.value)
          if (open) close()
        }}
        onKeyDown={onKey}
        aria-expanded={open}
        aria-controls={listId}
        aria-busy={busy}
        role="combobox"
        aria-autocomplete="none"
        aria-activedescendant={
          results && results.hits.length > 1 ? `${listId}-${active}` : undefined
        }
        spellCheck={false}
        autoComplete="off"
      />
      {open && (
        <div className="search-results" id={listId}>
          {error && <p className="search-empty">{error}</p>}
          {results && results.hits.length > 1 && (
            <>
              <p className="search-head">This could be any of these:</p>
              <ul role="listbox" aria-label="Pages this matches">
                {results.hits.map((h, i) => (
                  <li
                    key={`${h.kind}-${h.value}`}
                    id={`${listId}-${i}`}
                    role="option"
                    aria-selected={i === active}
                    className={i === active ? 'is-active' : undefined}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => {
                      // Before the input blurs, so the click lands.
                      e.preventDefault()
                      go(h)
                    }}
                  >
                    <span>{h.label}</span>
                    {h.detail && <span className="search-detail">{h.detail}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
          {results && results.hits.length === 0 && (
            <p className="search-empty">
              Nothing found
              {results.checked.length > 0 && <> — checked as {results.checked.join(', ')}</>}.
              {results.note && <span className="search-detail"> {results.note}.</span>}
            </p>
          )}
        </div>
      )}
    </div>
  )
}
